import React from 'react';
import { Row, Col, Card, Statistic, Spin, Progress, Button, Tag, Empty, Space } from 'antd';
import {
    UserOutlined,
    TeamOutlined,
    DollarOutlined,
    RiseOutlined,
    FallOutlined,
    BookOutlined,
    TrophyOutlined,
    BellOutlined,
    WarningOutlined,
    ArrowRightOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const Dashboard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Fetch dashboard summary data
    const { data: dashboardData, isLoading: isDashboardLoading } = useQuery('dashboard', async () => {
        const response = await api.get('/reports/dashboard');
        return response.data.data;
    });

    // Fetch monthly trend data
    const { data: monthlyTrendData, isLoading: isTrendLoading } = useQuery('monthlyTrend', async () => {
        const response = await api.get('/reports/charts/monthly-trend');
        return response.data.data;
    });

    // Fetch enrollment trend data
    const { data: enrollmentTrendData, isLoading: isEnrollmentLoading } = useQuery('enrollmentTrend', async () => {
        const response = await api.get('/reports/charts/enrollment-trend');
        return response.data.data;
    });

    // Fetch payment notifications
    const { data: notificationsData, isLoading: isNotificationsLoading } = useQuery('urgent-notifications', async () => {
        const response = await api.get('/payment-notifications/urgent');
        return response.data.data || [];
    });

    const isLoading = isDashboardLoading || isTrendLoading || isEnrollmentLoading;

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    const COLORS = ['#43e97b', '#fa709a', '#0088FE', '#FF8042', '#8884d8', '#82ca9d'];

    // Combined chart data for both currencies
    const chartDataCombined = [
        {
            name: 'IQD',
            [t('income')]: dashboardData?.finance?.byCurrency?.IQD?.income || 0,
            [t('expenses')]: dashboardData?.finance?.byCurrency?.IQD?.expense || 0,
        },
        {
            name: 'USD',
            [t('income')]: dashboardData?.finance?.byCurrency?.USD?.income || 0,
            [t('expenses')]: dashboardData?.finance?.byCurrency?.USD?.expense || 0,
        },
    ];

    // Combined pie chart data for both currencies
    const pieDataCombined = [
        { name: `${t('income')} (IQD)`, value: dashboardData?.finance?.byCurrency?.IQD?.income || 0 },
        { name: `${t('expenses')} (IQD)`, value: dashboardData?.finance?.byCurrency?.IQD?.expense || 0 },
        { name: `${t('income')} (USD)`, value: dashboardData?.finance?.byCurrency?.USD?.income || 0 },
        { name: `${t('expenses')} (USD)`, value: dashboardData?.finance?.byCurrency?.USD?.expense || 0 },
    ];

    // Use real API data for monthly trend
    const trendData = monthlyTrendData || [];

    // Use real API data for enrollment trend
    const enrollmentData = enrollmentTrendData || [];

    return (
        <div>
            <h1 style={{ marginBottom: 24, fontSize: '32px', fontWeight: 700, color: '#1e3a5f' }}>{t('dashboard')}</h1>

            {/* Statistics Cards */}
            <Row gutter={[24, 24]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-purple"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    {t('totalStudents')}
                                </div>
                                <div style={{ fontSize: '36px', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                                    {dashboardData?.students?.total || 0}
                                </div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' }}>
                                    {t('active')}: {dashboardData?.students?.active || 0}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <UserOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                        <Progress
                            percent={dashboardData?.students?.total ? Math.round((dashboardData?.students?.active / dashboardData?.students?.total) * 100) : 0}
                            showInfo={false}
                            strokeColor="#fff"
                            trailColor="rgba(255,255,255,0.3)"
                            strokeWidth={6}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-pink"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(240, 147, 251, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    {t('totalStaff')}
                                </div>
                                <div style={{ fontSize: '36px', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                                    {dashboardData?.staff?.total || 0}
                                </div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' }}>
                                    {t('active')}: {dashboardData?.staff?.active || 0}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <TeamOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                        <Progress
                            percent={dashboardData?.staff?.total ? Math.round((dashboardData?.staff?.active / dashboardData?.staff?.total) * 100) : 0}
                            showInfo={false}
                            strokeColor="#fff"
                            trailColor="rgba(255,255,255,0.3)"
                            strokeWidth={6}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card
                        bordered={false}
                        className="dashboard-gradient-card gradient-cyan"
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(79, 172, 254, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    {t('totalIncome')}
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff', lineHeight: 1.4, wordBreak: 'break-word' }}>
                                    IQD {(dashboardData?.finance?.byCurrency?.IQD?.income || 0).toLocaleString()}<br />
                                    ${(dashboardData?.finance?.byCurrency?.USD?.income || 0).toLocaleString()}
                                </div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' }}>
                                    {t('thisMonth')}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <DollarOutlined style={{ fontSize: '30px', color: '#fff' }} />
                            </div>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card
                        bordered={false}
                        className={`dashboard-gradient-card ${(dashboardData?.finance?.monthlyProfit || 0) >= 0 ? 'gradient-green' : 'gradient-sunset'}`}
                        style={{
                            borderRadius: '16px',
                            boxShadow: (dashboardData?.finance?.monthlyProfit || 0) >= 0
                                ? '0 8px 24px rgba(67, 233, 123, 0.25)'
                                : '0 8px 24px rgba(250, 112, 154, 0.25)',
                            height: '180px'
                        }}
                        bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', fontWeight: 500 }}>
                                    {t('netProfit')}
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff', lineHeight: 1.4, wordBreak: 'break-word' }}>
                                    IQD {(dashboardData?.finance?.byCurrency?.IQD?.profit || 0).toLocaleString()}<br />
                                    ${(dashboardData?.finance?.byCurrency?.USD?.profit || 0).toLocaleString()}
                                </div>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginTop: '8px' }}>
                                    {(dashboardData?.finance?.monthlyProfit || 0) >= 0 ? t('profit') : t('loss')}
                                </div>
                            </div>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '16px',
                                background: 'rgba(255,255,255,0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                {(dashboardData?.finance?.monthlyProfit || 0) >= 0 ?
                                    <RiseOutlined style={{ fontSize: '30px', color: '#fff' }} /> :
                                    <FallOutlined style={{ fontSize: '30px', color: '#fff' }} />
                                }
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Charts Section */}
            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                {/* Combined Bar Chart - IQD & USD Income vs Expenses */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600, color: '#1e3a5f' }}>
                                💰 {t('incomeVsExpenses')} (IQD & USD)
                            </span>
                        }
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                        }}
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={chartDataCombined}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" stroke="#666" />
                                <YAxis stroke="#666" />
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: '8px',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }}
                                />
                                <Legend />
                                <Bar dataKey={t('income')} fill="#43e97b" radius={[8, 8, 0, 0]} />
                                <Bar dataKey={t('expenses')} fill="#fa709a" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                {/* Combined Pie Chart - IQD & USD Distribution */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600, color: '#1e3a5f' }}>
                                📊 {t('financialDistribution')} (IQD & USD)
                            </span>
                        }
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                        }}
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={pieDataCombined}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={true}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={100}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {pieDataCombined.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: '8px',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                {/* Combined Chart - 6 Month Trend (IQD & USD) */}
                <Col xs={24}>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600, color: '#1e3a5f' }}>
                                <RiseOutlined style={{ marginRight: '8px' }} />
                                {t('monthlyTrend')} (IQD & USD)
                            </span>
                        }
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                        }}
                    >
                        <ResponsiveContainer width="100%" height={400}>
                            <LineChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="month" stroke="#666" />
                                <YAxis stroke="#666" />
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: '8px',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="incomeIQD"
                                    stroke="#43e97b"
                                    strokeWidth={3}
                                    dot={{ fill: '#43e97b', r: 5 }}
                                    activeDot={{ r: 7 }}
                                    name={`${t('income')} (IQD)`}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="expensesIQD"
                                    stroke="#fa709a"
                                    strokeWidth={3}
                                    dot={{ fill: '#fa709a', r: 5 }}
                                    activeDot={{ r: 7 }}
                                    name={`${t('expenses')} (IQD)`}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="incomeUSD"
                                    stroke="#0088FE"
                                    strokeWidth={3}
                                    strokeDasharray="5 5"
                                    dot={{ fill: '#0088FE', r: 5 }}
                                    activeDot={{ r: 7 }}
                                    name={`${t('income')} (USD)`}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="expensesUSD"
                                    stroke="#FF8042"
                                    strokeWidth={3}
                                    strokeDasharray="5 5"
                                    dot={{ fill: '#FF8042', r: 5 }}
                                    activeDot={{ r: 7 }}
                                    name={`${t('expenses')} (USD)`}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                {/* Line Chart - Student Enrollment Trend */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600, color: '#1e3a5f' }}>
                                <BookOutlined style={{ marginRight: '8px' }} />
                                {t('enrollmentTrend')}
                            </span>
                        }
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                        }}
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={enrollmentData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="month" stroke="#666" />
                                <YAxis stroke="#666" />
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: '8px',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="students"
                                    stroke="#667eea"
                                    strokeWidth={3}
                                    dot={{ fill: '#667eea', r: 6 }}
                                    activeDot={{ r: 8 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>

                {/* Payment Notifications Widget */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600, color: '#1e3a5f' }}>
                                <BellOutlined style={{ marginRight: '8px', color: '#ea580c' }} />
                                {t('paymentNotifications') || 'Payment Notifications'}
                            </span>
                        }
                        style={{
                            borderRadius: '16px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                        }}
                        extra={
                            notificationsData && notificationsData.length > 0 && (
                                <Button
                                    type="link"
                                    onClick={() => navigate('/payment-notifications')}
                                    icon={<ArrowRightOutlined />}
                                >
                                    {t('viewAll') || 'View All'}
                                </Button>
                            )
                        }
                    >
                        {isNotificationsLoading ? (
                            <Spin />
                        ) : notificationsData && notificationsData.length > 0 ? (
                            <div>
                                {notificationsData.slice(0, 5).map((notification, index) => (
                                    <div
                                        key={notification._id || index}
                                        style={{
                                            padding: '12px 0',
                                            borderBottom: index < Math.min(4, notificationsData.length - 1) ? '1px solid #f0f0f0' : 'none',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            gap: '12px'
                                        }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                                                {notification.student?.firstName} {notification.student?.lastName}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                                {notification.course?.name}
                                            </div>
                                            <div style={{ fontSize: '12px' }}>
                                                <span style={{ fontWeight: 600, color: '#dc2626' }}>
                                                    {notification.currency === 'IQD' ? 'IQD' : '$'} {notification.remainingAmount?.toLocaleString()}
                                                </span>
                                                {' '} {t('remaining') || 'remaining'}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <Tag
                                                color={
                                                    notification.type === 'payment-overdue'
                                                        ? 'red'
                                                        : notification.daysUntilDeadline <= 3
                                                            ? 'orange'
                                                            : 'blue'
                                                }
                                                icon={notification.type === 'payment-overdue' && <WarningOutlined />}
                                            >
                                                {notification.daysUntilDeadline < 0
                                                    ? `${Math.abs(notification.daysUntilDeadline)} ${t('daysOverdue') || 'days overdue'}`
                                                    : notification.daysUntilDeadline === 0
                                                        ? t('today') || 'Today'
                                                        : `${notification.daysUntilDeadline} ${t('daysLeft') || 'days left'}`}
                                            </Tag>
                                        </div>
                                    </div>
                                ))}
                                {notificationsData.length > 5 && (
                                    <div
                                        style={{
                                            padding: '12px 0',
                                            textAlign: 'center',
                                            color: '#1890ff',
                                            cursor: 'pointer',
                                            fontWeight: 500,
                                            fontSize: '12px'
                                        }}
                                        onClick={() => navigate('/payment-notifications')}
                                    >
                                        + {notificationsData.length - 5} {t('more') || 'more'} notifications
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Empty
                                description={t('noPaymentDeadlines') || 'No pending payment deadlines'}
                                style={{ marginTop: '20px' }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Dashboard;
